import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Pencil, Trash2, ShieldCheck, Ban, Camera, Users, UserCog, FolderKanban, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, adminDelete, adminPutProtected } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { isSubAdmin } from "../../lib/adminSections";
import { NavigationAnalysis } from "../../components/NavigationAnalysis";
import { SubAdminManager } from "../../components/SubAdminManager";
import { AdminPersonnel } from "../../components/AdminPersonnel";
import UserAvatar from "../../components/UserAvatar";
import AvatarCropper from "../../components/AvatarCropper";
import StatusBadge from "../../components/StatusBadge";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const { tf: t } = useLang();
  const { user: me } = useAuth();

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("usr_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("usr_title")}</h1>
      <p className="mt-2 text-sm text-ink-secondary">Ouvrez une fiche pour modifier les informations, gérer le comité de décision ou désactiver un compte.</p>

      {/* Délégation : création et gestion des sous-administrateurs (super admin uniquement) — toujours visible */}
      {!isSubAdmin(me) && (
        <>
          <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-sousadmins">Sous-administrateurs</div>
          <div className="mt-6" data-testid="users-panel-sousadmins"><SubAdminManager onChanged={load} /></div>
        </>
      )}

      {/* Personnel administrateur : droits + activité + export PDF/XLS (super admin uniquement).
          Clic sur une ligne = ouverture de sa fiche complète. */}
      {!isSubAdmin(me) && (
        <>
          <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-personnel">Personnel administrateur</div>
          <div className="mt-6" data-testid="users-panel-personnel">
            <AdminPersonnel onOpen={(p) => { const u = users.find((x) => x.id === p.id); if (u) setEditUser(u); else toast.error("Fiche introuvable dans la liste des utilisateurs."); }} />
          </div>
        </>
      )}

      {/* Section « utilisateurs » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-utilisateurs">Utilisateurs</div>
      <div className="mt-6 overflow-x-auto border border-white/10" data-testid="users-panel-utilisateurs">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
            <tr>
              <th className="p-4">{t("usr_name")}</th><th className="p-4">{t("usr_email")}</th>
              <th className="p-4">{t("usr_country")}</th><th className="p-4">{t("usr_role")}</th>
              <th className="p-4">Comité</th><th className="p-4">{t("usr_projects")}</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} onClick={() => setEditUser(u)} className={`cursor-pointer border-b border-white/5 transition-colors hover:bg-surface ${u.disabled ? "opacity-50" : ""}`} data-testid={`admin-user-${u.id}`}>
                <td className="p-4 text-white">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={u} size={32} testid={`admin-user-avatar-${u.id}`} />
                    <span>{u.first_name} {u.last_name}{u.disabled && <span className="ml-2 text-[10px] uppercase text-red-400">désactivé</span>}</span>
                  </div>
                </td>
                <td className="p-4 text-ink-secondary">{u.email}</td>
                <td className="p-4 text-ink-secondary">{u.country || "—"}</td>
                <td className="p-4">{u.role === "admin" ? <span className="text-cyan">{t("usr_admin")}</span> : u.role === "ambassador" ? <span className="text-amber-400">Ambassadeur</span> : <span className="text-ink-secondary">{t("usr_candidate")}</span>}</td>
                <td className="p-4">{u.committee ? <span className="inline-flex items-center gap-1 text-emerald-400"><ShieldCheck size={14} /> Oui</span> : <span className="text-ink-tertiary">—</span>}</td>
                <td className="p-4 tabular-nums text-white">{u.projects_count}</td>
                <td className="p-4 text-right">
                  <button onClick={(e) => { e.stopPropagation(); setEditUser(u); }} data-testid={`admin-user-open-${u.id}`}
                    className="aca-btn-secondary inline-flex items-center gap-1.5 text-white hover:text-cyan">
                    <Pencil size={13} /> Ouvrir la fiche
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <div className="p-12 text-center text-ink-secondary">Aucun utilisateur.</div>}
      </div>
      </>

      {editUser && <UserEditModal user={editUser} onClose={() => { setEditUser(null); load(); }} onDone={() => { setEditUser(null); load(); }} />}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, disabled }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</span>
      <input type={type} value={value ?? ""} onChange={onChange} data-testid={testid} disabled={disabled}
        className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan disabled:opacity-50" />
    </label>
  );
}

function UserEditModal({ user, onClose, onDone }) {
  const nav = useNavigate();
  const [uprojects, setUprojects] = useState(null); // dossiers de l'utilisateur (chargés à l'ouverture)
  useEffect(() => {
    api.get(`/admin/users/${user.id}/projects`).then((r) => setUprojects(r.data)).catch(() => setUprojects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);
  const [f, setF] = useState({
    first_name: user.first_name || "", last_name: user.last_name || "", email: user.email || "",
    phone: user.phone || "", country: user.country || "",
    role: user.role === "admin" ? "admin" : user.role || "user",
    committee: !!user.committee, disabled: !!user.disabled,
  });
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(user.photo || null);
  const [cropSrc, setCropSrc] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const isAdmin = user.role === "admin";
  const isSuper = !!user.is_super;

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image trop lourde (max 5 Mo).");
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result); // ouvre l'ajustement dans le rond
    reader.readAsDataURL(file);
  };

  const savePhoto = async (dataUrl) => {
    setCropSrc(null);
    setPhotoBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/avatar`, { image: dataUrl });
      setPhoto(data.photo);
      toast.success("Photo de profil enregistrée.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPhotoBusy(false); }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      if (!await adminDelete(`/admin/users/${user.id}/avatar`)) return;
      setPhoto(null);
      toast.success("Photo supprimée.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPhotoBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}`, {
        first_name: f.first_name, last_name: f.last_name, email: f.email,
        phone: f.phone, country: f.country, ...(isAdmin ? {} : { role: f.role }), disabled: f.disabled,
      });
      if (f.committee !== !!user.committee) {
        if (!await adminPutProtected(`/admin/users/${user.id}/committee`, { committee: f.committee })) { setSaving(false); setConfirming(false); return; }
      }
      toast.success("Fiche utilisateur enregistrée.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); setSaving(false); setConfirming(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer définitivement le compte de ${f.first_name} ${f.last_name} ? Cette action est irréversible.`)) return;
    try { if (!await adminDelete(`/admin/users/${user.id}`)) return; toast.success("Utilisateur supprimé."); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="user-edit-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-xl font-700 text-white">Fiche utilisateur</h3>
          <button onClick={onClose} data-testid="user-edit-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>

        {isSuper ? (
          <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="user-edit-super-note">
            Compte super administrateur : protégé, il ne peut pas être modifié ni supprimé depuis cette fiche.
          </p>
        ) : null}

        {/* Projets & dossiers de l'utilisateur — clic = ouvrir et gérer le dossier */}
        <div className="mb-6" data-testid="user-projects-section">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-cyan">
            <FolderKanban size={12} /> Projets &amp; dossiers
          </div>
          {uprojects === null ? (
            <div className="mt-2 text-xs text-ink-tertiary">Chargement des dossiers…</div>
          ) : uprojects.length === 0 ? (
            <div className="mt-2 rounded-lg border border-white/10 bg-surface px-4 py-3 text-xs text-ink-tertiary" data-testid="user-projects-empty">Aucune demande déposée par cet utilisateur.</div>
          ) : (
            <div className="mt-2 space-y-1.5">
              {uprojects.map((p) => (
                <button key={p.id} type="button" onClick={() => { onClose(); nav("/admin/groupes", { state: { openPid: p.id } }); }}
                  data-testid={`user-project-${p.id}`}
                  title={`Ouvrir et gérer le dossier ${p.ref} dans « Projets par utilisateur »`}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-surface px-4 py-3 text-left transition-colors hover:border-cyan/50 hover:bg-cyan/5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-600 text-white">{p.app_name || p.title}</div>
                    <div className="mt-0.5 text-[11px] text-ink-tertiary">{p.ref}{p.category ? ` · ${p.category}` : ""}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={p.status} />
                    {p.status === "SÉLECTIONNÉE" && <span className="text-[10px] font-600 tabular-nums text-cyan">{p.progress || 0}%</span>}
                    <ChevronRight size={15} className="text-ink-tertiary" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {!isSuper && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-surface p-4">
              <div className="relative shrink-0">
                <UserAvatar user={{ ...user, photo }} size={56} testid="user-edit-avatar" />
                <label className={`absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-cyan p-1.5 text-void transition-transform hover:scale-110 ${photoBusy ? "opacity-50" : ""}`} data-testid="user-edit-photo-btn" title="Télécharger / ajuster la photo">
                  <Camera size={12} />
                  <input type="file" accept="image/*" onChange={onPhotoPick} className="hidden" data-testid="user-edit-photo-input" disabled={photoBusy} />
                </label>
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-600 text-white">{user.first_name} {user.last_name}</div>
                <div className="truncate text-xs text-ink-tertiary">{photo ? "Photo de profil — cliquez sur l'appareil pour la repositionner" : "Aucune photo — initiales affichées"}</div>
                {photo && (
                  <button type="button" onClick={removePhoto} disabled={photoBusy} data-testid="user-edit-photo-delete"
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-600 text-red-400 transition-colors hover:text-red-300 disabled:opacity-50">
                    <Trash2 size={11} /> Supprimer la photo
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Prénom" value={f.first_name} onChange={set("first_name")} testid="user-edit-first-name" disabled={confirming} />
              <Field label="Nom" value={f.last_name} onChange={set("last_name")} testid="user-edit-last-name" disabled={confirming} />
            </div>
            <Field label="E-mail" value={f.email} onChange={set("email")} type="email" testid="user-edit-email" disabled={confirming} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Téléphone" value={f.phone} onChange={set("phone")} testid="user-edit-phone" disabled={confirming} />
              <Field label="Pays" value={f.country} onChange={set("country")} testid="user-edit-country" disabled={confirming} />
            </div>
            {isAdmin ? (
              <div>
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Rôle</span>
                <div className="flex items-center gap-2 rounded-xl border border-cyan/30 bg-cyan/5 px-4 py-3 text-sm text-cyan" data-testid="user-edit-role-static">
                  <ShieldCheck size={15} /> Administrateur — ses sections autorisées se gèrent dans l'onglet « Sous-administrateurs » (super admin).
                </div>
              </div>
            ) : (
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Rôle</span>
              <select value={f.role} onChange={set("role")} disabled={confirming} data-testid="user-edit-role"
                className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan disabled:opacity-50">
                <option value="user">Porteur de projet</option>
                <option value="ambassador">Ambassadeur</option>
              </select>
            </label>
            )}

            {!isAdmin && (
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-surface p-4" data-testid="user-edit-committee-row">
              <input type="checkbox" checked={f.committee} onChange={(e) => setF((s) => ({ ...s, committee: e.target.checked }))} disabled={confirming} data-testid="user-edit-committee" className="h-4 w-4 accent-emerald-500" />
              <span className="flex items-center gap-2 text-sm text-white"><ShieldCheck size={16} className="text-emerald-400" /> Membre du comité de décision</span>
            </label>
            )}

            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-surface p-4" data-testid="user-edit-disabled-row">
              <input type="checkbox" checked={f.disabled} onChange={(e) => setF((s) => ({ ...s, disabled: e.target.checked }))} disabled={confirming} data-testid="user-edit-disabled" className="h-4 w-4 accent-red-500" />
              <span className="flex items-center gap-2 text-sm text-white"><Ban size={16} className="text-red-400" /> Compte désactivé (connexion bloquée)</span>
            </label>

            {confirming ? (
              <div className="rounded-xl border border-cyan/40 bg-cyan/10 p-4" data-testid="user-edit-confirm-panel">
                <p className="text-sm text-white">Confirmez-vous l&apos;enregistrement de ces modifications ?</p>
                <div className="mt-4 flex gap-3">
                  <button onClick={save} disabled={saving} data-testid="user-edit-confirm-save"
                    className="aca-btn-primary flex-1 disabled:opacity-50">
                    {saving ? "Enregistrement…" : "Oui, confirmer"}
                  </button>
                  <button onClick={() => setConfirming(false)} disabled={saving} data-testid="user-edit-confirm-back"
                    className="aca-btn-secondary flex-1 text-white">
                    Retour
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 pt-2">
                <button onClick={() => setConfirming(true)} data-testid="user-edit-save"
                  className="aca-btn-primary flex-1">
                  Enregistrer
                </button>
                <button onClick={onClose} data-testid="user-edit-cancel"
                  className="aca-btn-secondary flex-1 text-white">
                  Annuler
                </button>
              </div>
            )}

            {!confirming && !isAdmin && (
              <button onClick={remove} data-testid="user-edit-delete"
                className="mt-2 inline-flex items-center gap-2 text-xs font-600 uppercase tracking-wide text-red-400 transition-colors hover:text-red-300">
                <Trash2 size={14} /> Supprimer ce compte
              </button>
            )}

            <NavigationAnalysis userId={user.id} />
          </div>
        )}
        {cropSrc && (
          <AvatarCropper src={cropSrc} onCancel={() => setCropSrc(null)} onValidate={savePhoto} testid="user-edit-cropper" />
        )}
      </motion.div>
    </div>
  );
}
